/**
 * Growth Portal PRISM Onboarding HTTP 客户端。
 */
import type {
  TPrismFollowupAnswerInput,
  TPrismQuestionField,
} from "./prism-dimension-scan";
import type { AudienceSegmentCardVO } from "@trex-2b/api/src/trex-api/trex-api";

import { getServerUrl } from "@trex-2b/libs/config/server-config";
import {
  ONBOARDING_FLOWS,
  reportGrowthPortalError,
} from "@trex-2b/libs/utils/bugsnag-report";
import { getAccessToken } from "@trex-2b/libs/utils/token-store";
import { Toast } from "@trex-2b/ui";

import { isRecord } from "@/lib/utils";

import {
  parsePrismReportPayload,
  type TMatchReportData,
} from "./match-report-parse-payload";
import {
  extractPersonasTop4CodesFromAnalyze,
  buildPrismFollowupAnswerEntries,
  PRISM_ANSWER_SKIP_VALUE,
  resolvePrismQuestionFieldFromFollowupDimensionKey,
  unwrapAnalyzePayload,
} from "./prism-dimension-scan";

export {
  buildPrismFollowupAnswerEntries,
  PRISM_ANSWER_SKIP_VALUE,
  resolvePrismQuestionFieldFromFollowupDimensionKey,
};
export type { TPrismFollowupAnswerInput, TPrismQuestionField };

const OS_DEVICE_ID_STORAGE_KEY = "trex2b_os_device_id";
const PRISM_ANALYZE_CACHE_KEY_PREFIX = "trex2b-prism-analyze-";

const PRISM_JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
} as const;

type TPrismAnalyzeResponse = Record<string, unknown>;

type TPrismReportResponse = Record<string, unknown>;

export type TPrismFallbackResponse = Record<string, unknown>;

type TPersonaIntelligenceSegment = {
  code: string;
  displayName: string;
  icon?: string;
  description?: string;
  featureTags?: string[];
  populationCount?: number;
  recommended?: boolean;
};

type TPersonaIntelligenceResponse = {
  success?: boolean;
  code?: string;
  message?: string;
  obj?: TPersonaIntelligenceSegment[];
};

export const PRISM_FALLBACK_PROJECT_TYPES = [
  "DeFi",
  "Perp",
  "RWA",
  "AI",
  "Meme",
  "Game",
  "DePIN",
  "Layer1",
  "Layer2",
  "Others",
] as const;

export const PRISM_FALLBACK_GROWTH_GOALS = [
  "Get users",
  "Drive volume",
  "Grow holders",
  "Build community",
] as const;

type TPrismFallbackProjectType = (typeof PRISM_FALLBACK_PROJECT_TYPES)[number];

type TPrismFallbackGrowthGoal = (typeof PRISM_FALLBACK_GROWTH_GOALS)[number];

export type TPrismFallbackRequestBody = {
  project_type: TPrismFallbackProjectType;
  growth_goal: TPrismFallbackGrowthGoal;
};

export type TPostPersonaIntelligenceRequest =
  | { codes: readonly string[] }
  | TPrismFallbackRequestBody;

export type TPersonaAudiencePayload = {
  audienceSegments: AudienceSegmentCardVO[];
  personas: string[];
};

const PRISM_ANALYZE_RATE_LIMITED_MESSAGE =
  "You've reached today's analysis limit (resets at 00:00 UTC).\nNeed more runs? Email us at marketing@trex.xyz";

const PRISM_ANALYZE_FAILED_FALLBACK = "Failed to analyze project. Try again.";
const PRISM_FALLBACK_FAILED_FALLBACK = "Failed to submit fallback. Try again.";
const PRISM_ANSWER_FAILED_FALLBACK = "Failed to submit answer. Try again.";
const PRISM_PERSONA_INTELLIGENCE_FAILED_FALLBACK =
  "Failed to load audience data. Try again.";
const PRISM_REPORT_FAILED_FALLBACK = "Failed to load match report. Try again.";

async function withPrismClientErrorToast<T>(
  fallback: string,
  request: () => Promise<T>,
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    Toast.show({
      type: "error",
      message: error instanceof Error ? error.message : fallback,
    });
    throw error;
  }
}

function readPrismApiErrorMessage(payload: unknown, fallback: string): string {
  if (isRecord(payload)) {
    const resultCode =
      (typeof payload.resultCode === "string" && payload.resultCode) ||
      (typeof payload.code === "string" && payload.code) ||
      "";
    if (resultCode === "RATE_LIMITED") {
      return PRISM_ANALYZE_RATE_LIMITED_MESSAGE;
    }
    const message =
      (typeof payload.message === "string" && payload.message.trim()) ||
      (typeof payload.error === "string" && payload.error.trim());
    if (message) {
      return message;
    }
  }

  if (typeof payload === "string" && payload.trim()) {
    return payload.trim();
  }

  return fallback;
}

function isAnalyzeBusinessFailure(payload: unknown): boolean {
  if (!isRecord(payload)) {
    return false;
  }
  if (payload.success === false) {
    return true;
  }
  return typeof payload.code === "string" && payload.code !== "0";
}

function parsePrismResponsePayload(responseText: string): unknown {
  if (!responseText.trim()) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
}

/** 入口 analyze 成功后写入；仅存 `{ obj }`，供 prism-stream extract 复用。 */
export function savePrismAnalyzeCache(
  applicationId: string,
  analyzeResponse: TPrismAnalyzeResponse,
): void {
  const id = applicationId.trim();
  if (typeof window === "undefined" || !id) return;

  const innerPayload = isRecord(analyzeResponse.obj)
    ? analyzeResponse.obj
    : isRecord(analyzeResponse.data)
      ? analyzeResponse.data
      : analyzeResponse;

  try {
    localStorage.setItem(
      `${PRISM_ANALYZE_CACHE_KEY_PREFIX}${id}`,
      JSON.stringify({ obj: innerPayload }),
    );
  } catch {
    /* ignore */
  }
}

export function loadPrismAnalyzeCache(
  applicationId: string,
): TPrismAnalyzeResponse | null {
  const id = applicationId.trim();
  if (typeof window === "undefined" || !id) return null;
  try {
    const raw = localStorage.getItem(`${PRISM_ANALYZE_CACHE_KEY_PREFIX}${id}`);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPrismAnalyzeCache(applicationId: string): void {
  const id = applicationId.trim();
  if (typeof window === "undefined" || !id) return;
  try {
    localStorage.removeItem(`${PRISM_ANALYZE_CACHE_KEY_PREFIX}${id}`);
  } catch {
    /* ignore */
  }
}

function isPersonaIntelligenceFallbackRequest(
  request: TPostPersonaIntelligenceRequest,
): request is TPrismFallbackRequestBody {
  return "project_type" in request && "growth_goal" in request;
}

/** 浏览器直连 PRISM 鉴权头（Bearer + os_device_id）。 */
export function buildPrismClientAuthHeaders(
  extraHeaders?: Record<string, string>,
): Record<string, string> {
  const accessToken = getAccessToken();
  const osDeviceId =
    typeof window === "undefined"
      ? ""
      : (window.localStorage.getItem(OS_DEVICE_ID_STORAGE_KEY) ?? "");

  return {
    ...PRISM_JSON_HEADERS,
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(osDeviceId ? { os_device_id: osDeviceId } : {}),
    ...extraHeaders,
  };
}

/** Step2：触发 PRISM 分析；失败时 Toast 并 rethrow。 */
export async function postPrismAnalyze(
  applicationId: string,
  handle: string,
): Promise<TPrismAnalyzeResponse> {
  return withPrismClientErrorToast(PRISM_ANALYZE_FAILED_FALLBACK, async () => {
    const response = await fetch(
      `${getServerUrl()}/v1/growth/onboarding/applications/${encodeURIComponent(applicationId)}/prism/analyze`,
      {
        method: "POST",
        headers: buildPrismClientAuthHeaders(),
        body: JSON.stringify({ handle: handle.trim() }),
        credentials: "include",
      },
    );

    const responseText = await response.text().catch(() => "");
    const payload = parsePrismResponsePayload(responseText);

    if (!response.ok || isAnalyzeBusinessFailure(payload)) {
      throw new Error(
        readPrismApiErrorMessage(
          payload,
          `PRISM analyze failed (${response.status})`,
        ),
      );
    }

    return (isRecord(payload) ? payload : {}) as TPrismAnalyzeResponse;
  });
}

/** Mode C：兜底表单提交；响应体含 `personas_top4`（与 report 同结构）。 */
export async function postPrismFallback(
  applicationId: string,
  body: TPrismFallbackRequestBody,
): Promise<TPrismFallbackResponse> {
  return withPrismClientErrorToast(PRISM_FALLBACK_FAILED_FALLBACK, async () => {
    const response = await fetch(
      `${getServerUrl()}/v1/growth/onboarding/applications/${encodeURIComponent(applicationId)}/prism/fallback`,
      {
        method: "POST",
        headers: buildPrismClientAuthHeaders(),
        body: JSON.stringify(body),
        credentials: "include",
      },
    );

    const responseText = await response.text().catch(() => "");
    const payload = parsePrismResponsePayload(responseText);
    if (!response.ok) {
      throw new Error(
        readPrismApiErrorMessage(
          payload,
          `PRISM fallback failed (${response.status})`,
        ),
      );
    }

    return (isRecord(payload) ? payload : {}) as TPrismFallbackResponse;
  });
}

type TPrismAnswerEntry = {
  field: TPrismQuestionField;
  value: string;
};

async function postPrismAnswerBatch(
  applicationId: string,
  answers: readonly TPrismAnswerEntry[],
): Promise<void> {
  await withPrismClientErrorToast(PRISM_ANSWER_FAILED_FALLBACK, async () => {
    const response = await fetch(
      `${getServerUrl()}/v1/growth/onboarding/applications/${encodeURIComponent(applicationId)}/prism/answer`,
      {
        method: "POST",
        headers: buildPrismClientAuthHeaders(),
        body: JSON.stringify({ answers }),
        credentials: "include",
      },
    );

    const responseText = await response.text().catch(() => "");
    const payload = parsePrismResponsePayload(responseText);
    if (!response.ok) {
      throw new Error(
        readPrismApiErrorMessage(
          payload,
          `PRISM answer failed (${response.status})`,
        ),
      );
    }
  });
}

/** Mode B：一次性提交全部追问答案，resume workflow。 */
export async function postPrismFollowupAnswers(
  applicationId: string,
  answers: readonly TPrismFollowupAnswerInput[],
): Promise<void> {
  await postPrismAnswerBatch(
    applicationId,
    buildPrismFollowupAnswerEntries(answers),
  );
}

/** Mode B：提交单条追问答案（测试或单维场景）。 */
export async function postPrismAnswer(
  applicationId: string,
  field: TPrismQuestionField,
  value: string,
): Promise<void> {
  await postPrismAnswerBatch(applicationId, [{ field, value }]);
}

/** Mode B：Skip 单条追问维度（测试或单维场景）。 */
export async function postPrismAnswerSkip(
  applicationId: string,
  field: TPrismQuestionField,
): Promise<void> {
  await postPrismAnswerBatch(applicationId, [
    { field, value: PRISM_ANSWER_SKIP_VALUE },
  ]);
}

/** Audience 步骤：按 persona codes 或 Mode C fallback 入参拉取 intelligence 卡片详情。 */
export async function postPersonaIntelligence(
  request: TPostPersonaIntelligenceRequest,
): Promise<TPersonaIntelligenceResponse> {
  return withPrismClientErrorToast(
    PRISM_PERSONA_INTELLIGENCE_FAILED_FALLBACK,
    async () => {
      const requestBody = isPersonaIntelligenceFallbackRequest(request)
        ? request
        : {
            codes: request.codes.map((code) => code.trim()).filter(Boolean),
          };

      if (
        !isPersonaIntelligenceFallbackRequest(request) &&
        request.codes.length === 0
      ) {
        throw new Error("codes is required");
      }

      const response = await fetch(
        `${getServerUrl()}/v1/growth/onboarding/persona-intelligence`,
        {
          method: "POST",
          headers: buildPrismClientAuthHeaders(),
          body: JSON.stringify(requestBody),
          credentials: "include",
        },
      );

      const responseText = await response.text().catch(() => "");
      const payload = parsePrismResponsePayload(responseText);
      if (!response.ok) {
        throw new Error(
          readPrismApiErrorMessage(
            payload,
            `Persona intelligence failed (${response.status})`,
          ),
        );
      }

      return (isRecord(payload) ? payload : {}) as TPersonaIntelligenceResponse;
    },
  );
}

function mapPersonaIntelligenceSegments(
  segments: TPersonaIntelligenceSegment[] | undefined,
): AudienceSegmentCardVO[] {
  return (segments ?? []) as AudienceSegmentCardVO[];
}

function resolvePersonasFromAudiencePayload(
  request: TPostPersonaIntelligenceRequest,
  audienceSegments: AudienceSegmentCardVO[],
): string[] {
  if (isPersonaIntelligenceFallbackRequest(request)) {
    return audienceSegments
      .map((segment) => segment.code?.trim())
      .filter((code): code is string => Boolean(code))
      .slice(0, 4);
  }

  return request.codes
    .map((code) => code.trim())
    .filter(Boolean)
    .slice(0, 4);
}

/** 从含 `personas_top4` 的 PRISM 载荷（report / fallback）提取 codes 并拉取受众卡片。 */
export async function fetchPersonaAudienceFromPrismPersonasTop4(
  prismPayload: Record<string, unknown> | null,
  errorContext?: Record<string, unknown>,
): Promise<TPersonaAudiencePayload> {
  const personaCodes = extractPersonasTop4CodesFromAnalyze(prismPayload);
  if (personaCodes.length === 0) {
    return { audienceSegments: [], personas: [] };
  }

  return fetchPersonaAudiencePayload({ codes: personaCodes }, errorContext);
}

/** Mode A/B（report personas_top4 codes）共用：拉取受众卡片并归一 personas。 */
export async function fetchPersonaAudiencePayload(
  request: TPostPersonaIntelligenceRequest,
  errorContext?: Record<string, unknown>,
): Promise<TPersonaAudiencePayload> {
  try {
    const personaIntelligenceResponse = await postPersonaIntelligence(request);
    const audienceSegments = mapPersonaIntelligenceSegments(
      personaIntelligenceResponse.obj,
    );
    return {
      audienceSegments,
      personas: resolvePersonasFromAudiencePayload(request, audienceSegments),
    };
  } catch (error) {
    reportGrowthPortalError(ONBOARDING_FLOWS.ADVERTISER_INTELLIGENCE, error, {
      stage: "persona_intelligence",
      ...errorContext,
    });
    return { audienceSegments: [], personas: [] };
  }
}

/** CTA 点击后：拉取 report 并可选拉取 persona-intelligence（禁止在组件 mount 时隐式调用）。 */
export async function fetchPrismMatchReportWithAudience(
  applicationId: string,
  handle: string,
  options?: {
    hasCachedAudienceData?: boolean;
    errorContext?: Record<string, unknown>;
  },
): Promise<{
  matchReportData: TMatchReportData;
  audiencePayload: TPersonaAudiencePayload;
}> {
  const reportPayload = await getPrismReport(applicationId, handle);
  const matchReportData = parsePrismReportPayload(reportPayload);
  let audiencePayload: TPersonaAudiencePayload = {
    audienceSegments: [],
    personas: [],
  };
  const personaCodes = extractPersonasTop4CodesFromAnalyze(reportPayload);
  if (personaCodes.length > 0 && !options?.hasCachedAudienceData) {
    audiencePayload = await fetchPersonaAudiencePayload(
      { codes: personaCodes },
      {
        application_id: applicationId,
        handle,
        step: "match_report",
        ...options?.errorContext,
      },
    );
  }
  return { matchReportData, audiencePayload };
}

/** Step3 等：获取 Match Report（后端要求 query `handle`）。 */
export async function getPrismReport(
  applicationId: string,
  handle: string,
): Promise<TPrismReportResponse> {
  return withPrismClientErrorToast(PRISM_REPORT_FAILED_FALLBACK, async () => {
    const trimmedHandle = handle.trim().replace(/^@+/, "");
    if (!trimmedHandle) {
      throw new Error("handle is required");
    }

    const reportUrl = new URL(
      `${getServerUrl()}/v1/growth/onboarding/applications/${encodeURIComponent(applicationId)}/prism/report`,
    );
    reportUrl.searchParams.set("handle", trimmedHandle);

    const response = await fetch(reportUrl.toString(), {
      method: "GET",
      headers: buildPrismClientAuthHeaders(),
      credentials: "include",
    });

    const responseText = await response.text().catch(() => "");
    const payload = parsePrismResponsePayload(responseText);
    if (!response.ok) {
      throw new Error(
        readPrismApiErrorMessage(
          payload,
          `PRISM report failed (${response.status})`,
        ),
      );
    }

    let parsed: TPrismReportResponse = {};
    if (responseText.trim()) {
      parsed = isRecord(payload)
        ? (payload as TPrismReportResponse)
        : { raw: responseText };
    }
    return unwrapAnalyzePayload(parsed);
  });
}
